import { Flex, HStack, Icon, Text } from "@chakra-ui/react";
import { IconType } from "react-icons";
import {
  FaPlayCircle,
  FaPauseCircle,
  FaLock,
  FaDotCircle,
} from "react-icons/fa";
import { RiEditCircleFill } from "react-icons/ri";
import { CAMPAIGN_STATUS_COLORS } from "../../../../common/constants/campaign";
import { UserSummary } from "../../../../common/types/common";
import IButton, { BUTTON } from "../../../../components/IButton";
import PublishButton from "./PublishButton";

function RoundedCardWithIcon({
  icon,
  color,
  label,
}: {
  icon: IconType;
  color: string;
  label: string;
}) {
  return (
    <Flex rounded="md" px="5" bg="white" alignItems="center" shadow="sm">
      <HStack alignItems="center">
        <Icon as={icon} color={color} />
        <Text fontSize="sm" fontWeight="400" color="gray.700">
          {label}
        </Text>
      </HStack>
    </Flex>
  );
}

function CampaignStatusBox({
  draft,
  active,
}: {
  draft: boolean;
  active: boolean;
}) {
  if (active)
    return (
      <RoundedCardWithIcon
        icon={FaPlayCircle}
        color={CAMPAIGN_STATUS_COLORS.Active}
        label="Journey is active"
      />
    );
  if (draft)
    return (
      <RoundedCardWithIcon
        icon={RiEditCircleFill}
        color={CAMPAIGN_STATUS_COLORS.Draft}
        label="Draft journey"
      />
    );
  return (
    <RoundedCardWithIcon
      icon={FaPauseCircle}
      color={CAMPAIGN_STATUS_COLORS.Inactive}
      label="Journey is inactive"
    />
  );
}

function SaveStatus({
  lastSaved,
  savingDraft,
  isDraft,
  currentUserId,
  updatedBy,
}: {
  lastSaved: string;
  savingDraft: boolean;
  isDraft: boolean;
  currentUserId: number;
  updatedBy: UserSummary | null;
}) {
  return (
    <Flex fontSize="12px" mr="10px" style={{ gap: "6px" }} alignItems="center">
      <Icon
        h="3"
        w="3"
        color={
          isDraft && currentUserId !== updatedBy?.id ? "red.500" : "green.500"
        }
        as={FaDotCircle}
      />
      {savingDraft ? (
        <Flex alignItems="center" fontWeight={600} mr="10px">
          Saving...
        </Flex>
      ) : (
        <Flex style={{ gap: "3px" }} alignItems="center">
          <Text>{isDraft ? "Saved" : `Published`}</Text>
          <Text fontWeight={600} whiteSpace="nowrap">
            {lastSaved}
          </Text>
          {isDraft && updatedBy && currentUserId !== updatedBy?.id && (
            <Flex fontSize="12px" style={{ gap: "4px" }} alignItems="center">
              <Text>by </Text>
              <Text
                fontWeight={600}
                maxWidth="100px"
                whiteSpace="nowrap"
                textOverflow="ellipsis"
                title={`${updatedBy?.name}`}
              >
                {updatedBy?.name}
              </Text>
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  );
}

export default function TopBar({
  updatedBy,
  isDraft,
  isActive,
  isLoadingSuccess,
  currentUserId,
  savingDraft,
  lastSaved,
  publishingCampaign,
  isCampaignEditable,
  onEditWarningOpen,
  onPublish,
  openReports,
}: {
  updatedBy: UserSummary | null;
  isDraft: boolean;
  isActive: boolean;
  isLoadingSuccess: boolean;
  currentUserId: number;
  savingDraft: boolean;
  lastSaved: string;
  publishingCampaign: boolean;
  isCampaignEditable: boolean;
  onEditWarningOpen: () => void;
  onPublish: (active: boolean) => void;
  openReports?: () => void;
}) {
  return (
    <Flex
      w="100%"
      justifyContent="space-between"
      p="3"
      shadow="sm"
      bg="oneOffs.reactFlow"
    >
      <CampaignStatusBox draft={isDraft} active={isActive} />
      <HStack justifyContent="flex-end">
        {isLoadingSuccess && isDraft === !!updatedBy && (
          <SaveStatus
            lastSaved={lastSaved}
            savingDraft={savingDraft}
            isDraft={isDraft}
            currentUserId={currentUserId}
            updatedBy={updatedBy}
          />
        )}

        {openReports && (
          <IButton variant={BUTTON.SECONDARY} onClick={openReports}>
            View Reports
          </IButton>
        )}

        {isLoadingSuccess &&
          (!isCampaignEditable ? (
            <IButton
              variant={BUTTON.PRIMARY}
              leftIcon={<FaLock />}
              onClick={onEditWarningOpen}
              isLoading={publishingCampaign}
              name="edit-campaign"
            >
              Edit journey
            </IButton>
          ) : (
            <PublishButton
              loading={publishingCampaign}
              publish={(active) => onPublish(active)}
            />
          ))}
      </HStack>
    </Flex>
  );
}
